import React, { useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { Form, Formik, useFormikContext } from 'formik';
import { isEqual, isNil, snakeCase, sortBy } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import type { FieldDefinitionDto } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import {
  hasAuthoredCondition,
  validateConditionsTree,
  withoutRqbIds,
} from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type {
  ClaimMilestoneDefinitionCreateJson,
  ClaimMilestoneDefinitionModel,
} from '~/server_shared/openapi/data-contracts';

import type { ScopeValues } from '../../ScopeFields/scopeFields';
import { scopeFieldsValidation } from '../../ScopeFields/scopeFields';
import ScopeFieldsFormik from '../../ScopeFields/ScopeFieldsFormik';
import TEXTS from '../texts';

import ClaimMilestoneConditionsQueryBuilderFormik from './ClaimMilestoneConditionsQueryBuilderFormik';

const VALIDATION_TOAST_ID = 'claim-milestone-errors';

// The create payload with every field filled in; the description is a string because a text field edits it, and
// the conditions are the tree the builder edits.
type ClaimMilestoneFormValues = Required<
  Omit<ClaimMilestoneDefinitionCreateJson, 'description' | 'conditions_json'>
> & {
  description: string;
  conditions_json: RuleGroupType | null;
};

interface ClaimMilestoneAddEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ClaimMilestoneDefinitionCreateJson) => Promise<void>;
  editedMilestone: ClaimMilestoneDefinitionModel | null;
  existingKeys: string[];
}

// No condition anywhere: no tree, an empty one, or one holding only empty groups, which the server refuses as a
// tree.
const holdsNoCondition = (tree: RuleGroupType | null): boolean => !hasAuthoredCondition(tree);

// The builder holds an empty group where nothing was stored, and RQB stamps an id on every node it mounts, so
// neither counts as an edit.
const isTreeUnchanged = (stored: RuleGroupType | null, current: RuleGroupType | null): boolean =>
  isNil(stored) || isNil(current) || holdsNoCondition(stored) || holdsNoCondition(current)
    ? holdsNoCondition(stored) && holdsNoCondition(current)
    : isEqual(withoutRqbIds(stored), withoutRqbIds(current));

// The scope the server validates the conditions against: a list counts only while its all-flag is off.
const effectiveScope = ({ is_all_lobs, lobs, is_all_sub_orgs, sub_organization_ids }: ScopeValues) => ({
  is_all_lobs,
  lobs: is_all_lobs ? [] : sortBy(lobs),
  is_all_sub_orgs,
  sub_organization_ids: is_all_sub_orgs ? [] : sortBy(sub_organization_ids),
});

interface ClaimMilestoneAddEditDialogInnerProps {
  isEdit: boolean;
  onCancel: () => void;
  onFieldsLoaded: (fields: FieldDefinitionDto[]) => void;
}

const ClaimMilestoneAddEditDialogInner: React.FC<ClaimMilestoneAddEditDialogInnerProps> = ({
  isEdit,
  onCancel,
  onFieldsLoaded,
}) => {
  const { values, isSubmitting } = useFormikContext<ClaimMilestoneFormValues>();
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  useSetDefaultFieldsOnChange(values.label, { key: snakeCase(values.label) }, null, isEdit);
  // A conditions error belongs to the whole tree, not to a focusable input: without the toast, a blocked Save
  // looks like a dead button.
  useFormikValidationErrorToast({ toastId: VALIDATION_TOAST_ID, title: TEXTS.validationErrorsTitle });

  return (
    <Form className="flex flex-col gap-12">
      <ValidationErrorToastContainer containerId={VALIDATION_TOAST_ID} />
      <TextFieldFormik id="label" name="label" label={TEXTS.labels.name} fullWidth />
      <TextFieldFormik id="key" name="key" label={TEXTS.labels.key} disabled={isEdit} fullWidth />
      <TextFieldFormik
        id="description"
        name="description"
        label={TEXTS.labels.description}
        helperText={TEXTS.descriptionHelperText}
        multiline
        rows={2}
        fullWidth
      />
      <ScopeFieldsFormik />
      <div className="flex flex-col gap-4">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.conditionsTitle}
        </Text>
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.conditionsDescription}
        </Text>
        <ClaimMilestoneConditionsQueryBuilderFormik
          isAllLobs={values.is_all_lobs}
          lobs={values.lobs}
          isAllSubOrgs={values.is_all_sub_orgs}
          subOrgIds={values.sub_organization_ids}
          onFieldsLoaded={onFieldsLoaded}
          onLoadingChange={setIsCatalogLoading}
        />
      </div>
      <SwitchFormik id="is_enabled" label={TEXTS.labels.active} disabled={isSubmitting} />
      <div className="flex justify-end">
        <Button variant="outlined" color="primary" className="mr-16" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting || isCatalogLoading}>
          {TEXTS.save}
        </Button>
      </div>
    </Form>
  );
};

const ClaimMilestoneAddEditDialog: React.FC<ClaimMilestoneAddEditDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editedMilestone,
  existingKeys,
}) => {
  const [conditionFields, setConditionFields] = useState<FieldDefinitionDto[]>([]);
  // The contract types the stored tree as an untyped object; it is the tree the builder saved.
  const storedConditions = (editedMilestone?.conditions_json ?? null) as RuleGroupType | null;
  const initialValues: ClaimMilestoneFormValues = {
    label: editedMilestone?.label ?? '',
    key: editedMilestone?.key ?? '',
    description: editedMilestone?.description ?? '',
    is_enabled: editedMilestone?.is_enabled ?? true,
    is_all_sub_orgs: editedMilestone?.is_all_sub_orgs ?? true,
    sub_organization_ids: editedMilestone?.sub_organization_ids ?? [],
    is_all_lobs: editedMilestone?.is_all_lobs ?? true,
    lobs: editedMilestone?.lobs ?? [],
    conditions_json: storedConditions,
  };

  // Stored conditions left alone under the scope they were saved with are not re-checked: one on a field the
  // catalog has since dropped would otherwise block every other edit. The server re-validates them when the
  // scope changes, so a changed scope re-checks them here too.
  const validate = (values: ClaimMilestoneFormValues) => {
    if (
      isTreeUnchanged(storedConditions, values.conditions_json) &&
      isEqual(effectiveScope(initialValues), effectiveScope(values))
    ) {
      return {};
    }
    const treeError = validateConditionsTree(values.conditions_json, conditionFields);
    return treeError ? { conditions_json: treeError } : {};
  };

  // An edit leaves untouched conditions out, so the server keeps them; a tree holding no condition is sent as null,
  // which removes them.
  const handleSubmit = ({ conditions_json, ...values }: ClaimMilestoneFormValues) =>
    onSubmit(
      editedMilestone && isTreeUnchanged(storedConditions, conditions_json)
        ? values
        : { ...values, conditions_json: holdsNoCondition(conditions_json) ? null : conditions_json }
    );

  return (
    <CardDialog
      isDialog
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={onClose}
      title={editedMilestone ? TEXTS.editMilestone : TEXTS.createMilestone}
    >
      <Formik<ClaimMilestoneFormValues>
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          label: Yup.string().trim().max(255).required(TEXTS.required),
          // The key is immutable once created, so only a new milestone's key is validated.
          ...(editedMilestone
            ? {}
            : {
                key: Yup.string()
                  .trim()
                  .max(128)
                  .notOneOf(existingKeys, TEXTS.keyMustBeUnique)
                  .required(TEXTS.required)
                  .matches(/^[a-z0-9_]+$/, TEXTS.keyMustMatchPattern)
                  .matches(/[a-z0-9]/, TEXTS.keyMustContainLetterOrNumber),
              }),
          ...scopeFieldsValidation,
        })}
        validate={validate}
        onSubmit={handleSubmit}
      >
        <ClaimMilestoneAddEditDialogInner
          isEdit={!!editedMilestone}
          onCancel={onClose}
          onFieldsLoaded={setConditionFields}
        />
      </Formik>
    </CardDialog>
  );
};

export default ClaimMilestoneAddEditDialog;
